import 'arrive';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

document.arrive('.block-carte-carburante-horizontal', { existing: true }, function (el) {
    // Evita di inizializzare l'animazione più volte sullo stesso elemento
    if (el.dataset.carteCarburanteInitialized) {
        return;
    }
    el.dataset.carteCarburanteInitialized = 'true';

    const label = el.querySelector('.block-carte-carburante-horizontal__label');
    const title = el.querySelector('.block-carte-carburante-horizontal__title');
    const switcher = el.querySelector('.block-carte-carburante-horizontal__switch');
    const carousels = gsap.utils.toArray('.block-carte-carburante-horizontal__carousel', el);
    const cards = gsap.utils.toArray('.block-carte-carburante-horizontal__carousel .card', el);
    const navGroups = gsap.utils.toArray('.carousel-navigation', el);

    if (!label || !title || !carousels.length || !switcher) {
        return;
    }

    const isMobileView = () => window.innerWidth < 992;

    // Nasconde gli elementi solo su desktop (l'animazione di ingresso è gestita da ScrollTrigger pin)
    // Su mobile gli elementi sono subito visibili
    if (!isMobileView()) {
        gsap.set([label, title, switcher, ...carousels], {
            opacity: 0,
            y: 50,
        });
    }

    // Funzioni per animazione orizzontale del track del carousel attivo
    let currentST = null;
    let currentTl = null;

    // Applica il padding-left calcolato a tutti i caroselli del blocco
    const applyPaddingToAllCarousels = () => {
        const blockWidth = el.getBoundingClientRect().width;
        carousels.forEach((carousel) => {
            const firstItem = carousel.querySelector('.card-item');
            let itemWidth = blockWidth;

            if (firstItem) {
                const rect = firstItem.getBoundingClientRect();
                itemWidth = Math.round(rect.width);
            }

            // aggiorna la variabile CSS sul singolo carosello
            carousel.style.setProperty('--carousel-width', `${itemWidth}px`);
        });
    };

    const buildHorizontalTimeline = (targetId) => {
        // pulisci eventuali timeline/ScrollTrigger precedenti
        if (currentST) { currentST.kill(); currentST = null; }
        if (currentTl) { currentTl.kill(); currentTl = null; }

        // assicurati che il padding sia aggiornato per tutti i caroselli
        applyPaddingToAllCarousels();

        const panel = el.querySelector(`#${targetId}`);
        if (!panel) return;
        const track = panel.querySelector('.cards-track');
        const items = gsap.utils.toArray('.card-item', track);
        if (!track || !items.length) return;

        // calcolo dello step orizzontale usando il delta tra i primi due item (responsive)
        let stepX = 0;
        if (items.length > 1) {
            const rect0 = items[0].getBoundingClientRect();
            const rect1 = items[1].getBoundingClientRect();
            stepX = Math.round(rect1.left - rect0.left);
        } else {
            // fallback: larghezza del primo elemento
            const rect0 = items[0].getBoundingClientRect();
            stepX = Math.round(rect0.width);
        }

        // timeline orizzontale: muove il track a sinistra
        currentTl = gsap.timeline();

        // spacer verticale basato sul numero di slides: 50px per slide sia prima che dopo
        const slidesCount = items.length;
        const bufferPerSlide = 50; // px
        const spacerStart = slidesCount * bufferPerSlide;
        const spacerEnd = slidesCount * bufferPerSlide;

        // aggiungiamo un tween fittizio per creare tempo nella timeline legata allo scroll
        currentTl.to({}, { duration: spacerStart / 1000, ease: 'none' });

        // poi il movimento orizzontale del track
        currentTl.to(track, {
            x: -stepX * (items.length - 1),
            ease: 'none',
            onUpdate: () => {
                // Aggiorna prev/current/next quando il centro della card si sposta del 25% verso dx/sx
                const viewport = el.getBoundingClientRect();
                const viewportCenter = viewport.left + viewport.width / 2;
                const threshold = viewport.width * 0.25; // 25% della larghezza del blocco

                // Trova l'item il cui centro è più vicino al centro viewport
                let closestIndex = 0;
                let minDelta = Number.POSITIVE_INFINITY;
                items.forEach((it, i) => {
                    const r = it.getBoundingClientRect();
                    const center = r.left + r.width / 2;
                    const delta = Math.abs(center - viewportCenter);
                    if (delta < minDelta) {
                        minDelta = delta;
                        closestIndex = i;
                    }
                });

                // Verifica se la differenza supera la soglia del 25% rispetto al viewport
                if (minDelta <= threshold) {
                    updateItemStates(panel, closestIndex);
                    // aggiorna anche i dots per il carosello corrente
                    updateDotsActiveState(targetId, closestIndex);
                }
            },
            onStart: () => {
                // appena parte il translate, imposta lo stato iniziale
                updateItemStates(panel, 0);
            }
        });

        // spacer finale dopo che lo scorrimento orizzontale è terminato
        currentTl.to({}, { duration: spacerEnd / 1000, ease: 'none' });

        // ScrollTrigger con pinning
        currentST = ScrollTrigger.create({
            trigger: el,
            start: 'top 12px',
            // aggiungiamo entrambi gli spacer alla durata verticale totale del trigger
            end: () => `+=${(Math.max(1, items.length - 1) * 800) + spacerStart + spacerEnd}`,
            pin: true,
            scrub: 1,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            animation: currentTl,
            onEnter: () => {
                gsap.to([label, title, switcher, ...carousels], {
                    opacity: 1,
                    y: 0,
                    stagger: 0.2,
                    duration: 0.8,
                    ease: 'power2.out',
                });
            },
            onLeaveBack: () => {
                gsap.to([label, title, switcher, ...carousels], {
                    opacity: 0,
                    y: 50,
                    duration: 0.4,
                    ease: 'power2.in',
                });
            }
        });
    };

    // inizializza sul pannello attivo di default
    const defaultActive = el.querySelector('.block-carte-carburante-horizontal__carousel.active');
    applyPaddingToAllCarousels();
    if (isMobileView()) {
        carousels.forEach(carousel => setupTouchSwipe(carousel));
        // Animazione di ingresso su mobile (nessun pin, solo fade-in al viewport)
        ScrollTrigger.create({
            trigger: el,
            start: 'top 80%',
            once: true,
            onEnter: () => {
                gsap.to([label, title, switcher, ...carousels], {
                    opacity: 1,
                    y: 0,
                    stagger: 0.2,
                    duration: 0.8,
                    ease: 'power2.out',
                });
            }
        });
    } else if (defaultActive) {
        buildHorizontalTimeline(defaultActive.id);
    }

    // ricalcola padding/timeline su resize e aggiorna ScrollTrigger
    const handleResize = () => {
        applyPaddingToAllCarousels();
        if (isMobileView()) {
            if (currentST) { currentST.kill(); currentST = null; }
            if (currentTl) { currentTl.kill(); currentTl = null; }
            carousels.forEach(carousel => setupTouchSwipe(carousel));
        } else {
            const active = el.querySelector('.block-carte-carburante-horizontal__carousel.active');
            if (active) buildHorizontalTimeline(active.id);
            ScrollTrigger.refresh();
        }
    };
    window.addEventListener('resize', handleResize);

    // Gestione switch tra Aziende/Privati
    const switchBtns = gsap.utils.toArray('.switch-btn', switcher);
    
    // Inizializza posizione thumb al caricamento
    const activeBtn = switcher.querySelector('.switch-btn.active') || switchBtns[0];
    if (activeBtn) {
        const rect = activeBtn.getBoundingClientRect();
        const parentRect = switcher.getBoundingClientRect();
        const width = rect.width;
        const left = rect.left - parentRect.left;

        // Imposta i valori iniziali delle CSS vars
        switcher.style.setProperty('--switch-width', `${width}px`);
        switcher.style.setProperty('--switch-pos', `${left}px`);
    }

    const setActive = (targetId) => {
        // switch buttons
        switchBtns.forEach((btn) => {
            const isActive = btn.dataset.target === targetId;
            btn.classList.toggle('active', isActive);
            btn.setAttribute('aria-selected', String(isActive));
            btn.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        // 👉 nuovo blocco: aggiorna la posizione e larghezza del thumb dinamicamente
        if (switcher) {
            const activeBtn = switcher.querySelector('.switch-btn.active');
            if (activeBtn) {
                const rect = activeBtn.getBoundingClientRect();
                const parentRect = switcher.getBoundingClientRect();
                const width = rect.width;
                const left = rect.left - parentRect.left;

                // ✅ Animazione fluida con GSAP
                gsap.to(switcher, {
                    duration: 0.4,
                    ease: 'power2.out',
                    '--switch-pos': `${left}px`,
                    '--switch-width': `${width}px`
                });
            }
        }

        // carousels
        carousels.forEach((c) => {
            const isActive = c.id === targetId;
            c.classList.toggle('active', isActive);
            c.setAttribute('aria-hidden', String(!isActive));
        });

        // navigation groups (dots)
        navGroups.forEach((nav) => {
            const isActive = nav.dataset.targetContent === targetId;
            nav.classList.toggle('active', isActive);
        });

        // ensure the first item is current in the target carousel if not already set
        const targetCarousel = el.querySelector(`#${targetId}`);
        if (targetCarousel) {
            const hasCurrent = !!targetCarousel.querySelector('.card-item.current');
            if (!hasCurrent) {
                updateItemStates(targetCarousel, 0);
            }
        }

        // reset cards visibility inside newly active carousel
        const activeCards = gsap.utils.toArray(`#${targetId} .card`, el);
        gsap.set(activeCards, { opacity: 1, y: 0, scale: 1 });

        // ricostruisci timeline per il carosello attivo (desktop) o setup touch (mobile)
        if (!isMobileView()) {
            buildHorizontalTimeline(targetId);
            ScrollTrigger.refresh();
        } else {
            const carousel = el.querySelector(`#${targetId}`);
            if (carousel) {
                setupTouchSwipe(carousel);
                applyPaddingToAllCarousels();
                moveCarouselToIndex(carousel, 0);
            }
        }
    };


    switchBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const targetId = btn.dataset.target;
            if (!targetId) return;
            setActive(targetId);
        });
    });

    // Navigazione next/prev basata su width card + 40px di gap
    const getActiveCarousel = () => el.querySelector('.block-carte-carburante-horizontal__carousel.active');

    const getStepForCarousel = (carousel) => {
        const track = carousel.querySelector('.cards-track');
        const items = track ? track.querySelectorAll('.card-item') : null;
        if (!items || items.length === 0) return 0;
        if (items.length > 1) {
            const rect0 = items[0].getBoundingClientRect();
            const rect1 = items[1].getBoundingClientRect();
            return Math.round(rect1.left - rect0.left);
        }
        const rect0 = items[0].getBoundingClientRect();
        return Math.round(rect0.width);
    };

    const moveCarouselBy = (carousel, direction = 1) => {
        if (!carousel) return;
        const track = carousel.querySelector('.cards-track');
        const items = gsap.utils.toArray('.card-item', track);
        if (!track || !items.length) return;
        const step = getStepForCarousel(carousel);
        const currentX = gsap.getProperty(track, 'x') || 0;
        const maxIndex = Math.max(0, items.length - 1);
        let index = Math.round(Math.abs(currentX) / step);
        index = Math.min(Math.max(index + direction, 0), maxIndex);
        moveCarouselToIndex(carousel, index);
    };

    // muovi a indice specifico e aggiorna classi prev/current/next
    const updateItemStates = (carousel, currentIndex) => {
        const items = gsap.utils.toArray('.card-item', carousel);
        items.forEach((it, i) => {
            it.classList.remove('prev', 'current', 'next');
            if (i < currentIndex) it.classList.add('prev');
            else if (i === currentIndex) it.classList.add('current');
            else it.classList.add('next');
        });
    };

    const moveCarouselToIndex = (carousel, targetIndex) => {
        if (!carousel) return;
        const track = carousel.querySelector('.cards-track');
        const items = gsap.utils.toArray('.card-item', track);
        if (!track || !items.length) return;
        const step = getStepForCarousel(carousel);
        const maxIndex = Math.max(0, items.length - 1);
        const clampedIndex = Math.min(Math.max(targetIndex, 0), maxIndex);
        const newX = -clampedIndex * step;
        gsap.to(track, { x: newX, duration: 0.5, ease: 'power2.out' });
        updateItemStates(carousel, clampedIndex);
        // aggiorna anche i dots per il carosello attivo
        const targetId = carousel.id;
        updateDotsActiveState(targetId, clampedIndex);
    };

    // Touch swipe per mobile: swipe orizzontale diretto sul track
    // Dichiarata come function (hoisted) perché viene chiamata prima della sua posizione nel file
    function setupTouchSwipe(carousel) {
        const track = carousel.querySelector('.cards-track');
        if (!track || track.dataset.touchInitialized === 'true') return;
        track.dataset.touchInitialized = 'true';

        let startX = 0;
        let startY = 0;
        let isHorizontal = null;

        track.addEventListener('touchstart', (e) => {
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
            isHorizontal = null;
        }, { passive: true });

        track.addEventListener('touchmove', (e) => {
            if (isHorizontal === null) {
                const dx = Math.abs(e.touches[0].clientX - startX);
                const dy = Math.abs(e.touches[0].clientY - startY);
                isHorizontal = dx > dy;
            }
            // blocca lo scroll verticale solo se stiamo scorrendo orizzontalmente
            if (isHorizontal) e.preventDefault();
        }, { passive: false });

        track.addEventListener('touchend', (e) => {
            const dx = e.changedTouches[0].clientX - startX;
            if (Math.abs(dx) > 40) {
                moveCarouselBy(carousel, dx < 0 ? 1 : -1);
            }
            isHorizontal = null;
        });
    }

    // bind click su card-item: porta la card cliccata a current
    const bindSlideClickNavigation = () => {
        carousels.forEach((carousel) => {
            carousel.addEventListener('click', (e) => {
                const item = e.target.closest('.card-item');
                if (!item) return;
                if (!carousel.classList.contains('active')) return;
                const items = Array.from(carousel.querySelectorAll('.card-item'));
                const targetIndex = items.indexOf(item);
                if (targetIndex === -1) return;
                moveCarouselToIndex(carousel, targetIndex);
                // dots per il carosello attivo
                updateDotsActiveState(carousel.id, targetIndex);
            });
        });
    };

    // ensure each carousel has its initial current/prev/next state on load
    carousels.forEach((carousel) => {
        const hasCurrent = !!carousel.querySelector('.card-item.current');
        const items = carousel.querySelectorAll('.card-item');
        if (!hasCurrent && items.length) {
            updateItemStates(carousel, 0);
        }
    });

    bindSlideClickNavigation();

    // bind ai pulsanti next/prev nel blocco (richiede data attributes sugli elementi di controllo)
    const nextBtns = gsap.utils.toArray('[data-carousel-next]', el);
    const prevBtns = gsap.utils.toArray('[data-carousel-prev]', el);
    nextBtns.forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        moveCarouselBy(getActiveCarousel(), +1);
    }));
    prevBtns.forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        moveCarouselBy(getActiveCarousel(), -1);
    }));

    // Click sui dots: muovi il carosello target all'indice selezionato e aggiorna i dots
    const dotNavs = gsap.utils.toArray('.carousel-navigation', el);
    const updateDotsActiveState = (targetId, index) => {
        dotNavs.forEach((nav) => {
            const isTarget = nav.dataset.targetContent === targetId;
            if (!isTarget) return;
            const dots = gsap.utils.toArray('.dot-btn', nav);
            dots.forEach((d, i) => d.classList.toggle('active', i === index));
        });
    };

    dotNavs.forEach((nav) => {
        nav.addEventListener('click', (e) => {
            const btn = e.target.closest('.dot-btn');
            if (!btn) return;
            e.preventDefault();
            const targetId = nav.dataset.targetContent;
            const carousel = el.querySelector(`#${targetId}`);
            if (!carousel) return;
            const idxAttr = btn.getAttribute('data-slide-index');
            const targetIndex = parseInt(idxAttr, 10);
            if (Number.isNaN(targetIndex)) return;
            moveCarouselToIndex(carousel, targetIndex);
            updateDotsActiveState(targetId, targetIndex);
        });
    });

});
